import { api, ApiError, type ProjectMeta, type SessionUser } from '../io/api';
import { showUpgradeNotice } from './upgradeNotice';

/**
 * Projektübersicht (wie in Figma): Grid aus Karten mit Vorschaubild, Name und
 * „zuletzt bearbeitet“. Aktionen: neu, öffnen, umbenennen, duplizieren, löschen.
 * Diese Klasse ersetzt den Inhalt des App-Roots komplett (kein Editor-DOM).
 * Gratis-Tarif: `limit` Projekte (Server-vorgegeben, standardmäßig 3).
 */

export class Dashboard {
  private projects: ProjectMeta[] = [];
  private limit = 3;
  private filter = '';
  private loaded = false;

  constructor(
    private root: HTMLElement,
    private user: SessionUser
  ) {
    this.render();
    void this.load();
  }

  private async load(): Promise<void> {
    try {
      const res = await api.listProjects();
      this.projects = res.projects;
      this.limit = res.limit;
      this.loaded = true;
      this.render();
    } catch (err) {
      if (err instanceof ApiError && err.unauthorized) {
        // Sitzung abgelaufen → neu anmelden
        location.reload();
        return;
      }
      console.error('Projekte konnten nicht geladen werden:', err);
      this.loaded = true;
      this.render();
    }
  }

  private render(): void {
    const root = this.root;
    root.replaceChildren();
    root.className = 'dash-root';

    // ---- Kopfzeile ----
    const header = document.createElement('header');
    header.className = 'dash-header';
    const logo = document.createElement('div');
    logo.className = 'logo';
    logo.innerHTML =
      '<svg viewBox="0 0 100 100" width="22" height="22"><rect x="8" y="30" width="70" height="45" rx="8" fill="#e0552c"/><circle cx="28" cy="78" r="9" fill="#222"/><circle cx="68" cy="78" r="9" fill="#222"/></svg><span>CampSketch</span>';
    const userChip = document.createElement('span');
    userChip.className = 'dash-user';
    userChip.title = this.user.email;
    userChip.textContent = this.user.name;
    const logoutBtn = document.createElement('button');
    logoutBtn.className = 'top-btn';
    logoutBtn.textContent = 'Abmelden';
    logoutBtn.onclick = () => {
      void api
        .logout()
        .catch(() => undefined)
        .finally(() => location.reload());
    };
    header.append(logo, mkSpacer(), userChip, logoutBtn);

    // ---- Hauptbereich ----
    const main = document.createElement('main');
    main.className = 'dash-main';

    const headRow = document.createElement('div');
    headRow.className = 'dash-head-row';
    const h1 = document.createElement('h1');
    h1.textContent = 'Meine Projekte';
    const search = document.createElement('input');
    search.className = 'dash-search';
    search.type = 'search';
    search.placeholder = 'Projekte suchen …';
    search.oninput = () => {
      this.filter = search.value.trim().toLowerCase();
      this.renderGrid();
    };
    const newBtn = document.createElement('button');
    newBtn.className = 'dash-new-btn';
    newBtn.textContent = '+ Neues Projekt';
    newBtn.onclick = () => void this.createProject();
    const usage = document.createElement('span');
    usage.className = 'dash-usage';
    usage.title = `Gratis-Tarif: ${this.limit} Projekte`;
    headRow.append(h1, mkSpacer(), search, usage, newBtn);

    const grid = document.createElement('div');
    grid.className = 'dash-grid';

    const errorBox = document.createElement('p');
    errorBox.className = 'dash-empty';
    errorBox.hidden = true;

    main.append(headRow, grid, errorBox);
    root.append(header, main);
    this.gridEl = grid;
    this.errorEl = errorBox;
    this.usageEl = usage;
    this.renderGrid();
  }

  private gridEl: HTMLElement | null = null;
  private errorEl: HTMLElement | null = null;
  private usageEl: HTMLElement | null = null;

  private renderUsage(): void {
    if (!this.usageEl) return;
    const atLimit = this.projects.length >= this.limit;
    this.usageEl.textContent = `${this.projects.length} / ${this.limit} Projekte`;
    this.usageEl.dataset.state = atLimit ? 'limit' : 'ok';
    this.usageEl.title = atLimit
      ? `Gratis-Tarif ausgeschöpft (${this.limit} Projekte) — für neue Projekte bitte upgraden`
      : `Gratis-Tarif: ${this.limit} Projekte`;
  }

  private renderGrid(): void {
    if (!this.gridEl || !this.errorEl) return;
    const filter = this.filter;
    const visible = filter ? this.projects.filter((p) => p.name.toLowerCase().includes(filter)) : this.projects;
    this.gridEl.replaceChildren(...visible.map((p) => this.card(p)));
    this.renderUsage();
    // Leermeldung nur, wenn wirklich nichts (mehr) da ist
    const showEmpty = this.loaded && (this.projects.length === 0 || visible.length === 0);
    this.errorEl.hidden = !showEmpty;
    this.errorEl.textContent =
      this.projects.length === 0 ? 'Noch keine Projekte — leg mit „Neues Projekt“ los. Bestehende .campsketch.json-Dateien kannst du im Editor über „Import“ übernehmen.' : 'Keine Projekte für diese Suche.';
  }

  private card(p: ProjectMeta): HTMLElement {
    const card = document.createElement('article');
    card.className = 'dash-card';

    const thumb = document.createElement('div');
    thumb.className = 'dash-thumb';
    if (p.thumbnail) {
      const img = document.createElement('img');
      img.src = p.thumbnail;
      img.alt = `Vorschau von „${p.name}“`;
      img.loading = 'lazy';
      thumb.appendChild(img);
    } else {
      const ph = document.createElement('div');
      ph.className = 'dash-thumb-placeholder';
      ph.innerHTML =
        '<svg viewBox="0 0 100 100" width="42" height="42"><rect x="8" y="30" width="70" height="45" rx="8" fill="none" stroke="#b6bec5" stroke-width="5"/><circle cx="28" cy="78" r="7" fill="#b6bec5"/><circle cx="68" cy="78" r="7" fill="#b6bec5"/></svg>';
      thumb.appendChild(ph);
    }

    const meta = document.createElement('div');
    meta.className = 'dash-card-meta';
    const name = document.createElement('div');
    name.className = 'dash-name';
    name.textContent = p.name;
    name.title = p.name;
    const date = document.createElement('div');
    date.className = 'dash-date';
    date.textContent = `zuletzt bearbeitet ${relativeTime(p.updatedAt)}`;
    const actions = document.createElement('div');
    actions.className = 'dash-actions';
    actions.append(
      this.actionBtn('✏️', 'Umbenennen', () => void this.rename(p)),
      this.actionBtn('⧉', 'Duplizieren', () => void this.duplicate(p)),
      this.actionBtn('🗑', 'Löschen', () => void this.remove(p))
    );
    meta.append(name, date, actions);

    card.append(thumb, meta);
    card.onclick = () => location.assign(`/p/${p.id}`);
    return card;
  }

  private actionBtn(label: string, title: string, fn: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = 'dash-action-btn';
    b.textContent = label;
    b.title = title;
    b.onclick = (e) => {
      e.stopPropagation();
      fn();
    };
    return b;
  }

  private async createProject(): Promise<void> {
    try {
      const { project } = await api.createProject('Unbenanntes Projekt');
      location.assign(`/p/${project.id}`);
    } catch (err) {
      this.report(err, 'Projekt konnte nicht angelegt werden');
    }
  }  private async rename(p: ProjectMeta): Promise<void> {
    const name = prompt('Projekt umbenennen', p.name)?.trim();
    if (!name || name === p.name) return;
    try {
      await api.saveProject(p.id, { name });
      p.name = name;
      this.renderGrid();
    } catch (err) {
      this.report(err, 'Umbenennen fehlgeschlagen');
    }
  }

  private async duplicate(p: ProjectMeta): Promise<void> {
    try {
      const { project: record } = await api.getProject(p.id);
      const name = `${record.name} (Kopie)`.slice(0, 80);
      const { project } = await api.createProject(name, record.data, record.thumbnail);
      location.assign(`/p/${project.id}`);
    } catch (err) {
      this.report(err, 'Duplizieren fehlgeschlagen');
    }
  }

  private async remove(p: ProjectMeta): Promise<void> {
    if (!confirm(`„${p.name}“ wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    try {
      await api.deleteProject(p.id);
      this.projects = this.projects.filter((x) => x.id !== p.id);
      this.renderGrid();
    } catch (err) {
      this.report(err, 'Löschen fehlgeschlagen');
    }
  }

  private report(err: unknown, fallback: string): void {
    if (err instanceof ApiError && err.status === 402) {
      showUpgradeNotice(err.message);
      return;
    }
    console.error(err);
    alert(err instanceof ApiError ? err.message : fallback);
  }
}

/** Flexibler Abstandhalter für Kopfzeilen. */
function mkSpacer(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'dash-spacer';
  return el;
}

/** „gerade eben“, „vor 5 Min.“, „vor 3 Std.“, „vor 2 Tagen“, sonst Datum. */
function relativeTime(timestamp: number): string {  const diff = Date.now() - timestamp;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
  return new Date(timestamp).toLocaleDateString('de-DE');
}
